import Log from './Log';
import { contracts } from '../config/blockchain';
import { ITEM_TYPES, AUCTION_PRICE_ESTIMATE_MULTIPLIER, REGULAR_AUCTION_TIME_ADDITION, PROVIDERS } from '../config/constants';
import { INSUFFICIENT_FUNDS } from '../config/strings';

export const getEstimatedGasForTransaction = (method, id, payload) =>
  new Promise((resolve, reject) => {
    method.estimateGas(id, payload, (error, estimatedGas) => {
      if (error) {
        reject(`Failed when tried to estimate gas for contract: ${error}.`);
      } else {
        resolve(estimatedGas);
      }
    });
  });

export const getAuctionPrice = (auctionItem) =>
  new Promise((resolve, reject) => {
    // Regular auctions will fail if sending the current price (the price increases in every block and a new transaction can only be in a new block)
    const secsInFuture = (auctionItem.endPrice > auctionItem.startPrice) ? REGULAR_AUCTION_TIME_ADDITION : 0;

    contracts.stickItemTypeAuction.getPrice(auctionItem.id, secsInFuture, (error, currentPrice) => {
      if (error) {
        reject(`Failed when tried to getAuctionPrice: ${error}.`);
      } else {
        resolve(currentPrice.toNumber());
      }
    });
  });

export const buyMarketItem = (marketItem, myWalletAddress, web3, eventUserId, buyCallback) =>
  new Promise((resolve, reject) => {
    if (marketItem.type === ITEM_TYPES.AUCTION) {
      buyAuctionItem(marketItem, myWalletAddress, web3, eventUserId, buyCallback).then((txHash) => {
        resolve(txHash);
      }, (error) => {
        reject(error);
      });
    } else {
      buySaleItem(marketItem, myWalletAddress, web3, eventUserId, buyCallback).then((txHash) => {
        resolve(txHash);
      }, (error) => {
        reject(error);
      });
    }
  });

export const buyAuctionItem = (auctionItem, myWalletAddress, web3, eventUserId, buyCallback) =>
  new Promise((resolve, reject) => {
    getAuctionPrice(auctionItem).then((currentPrice) => {
      // Sometimes Infura (the MetaMask provider) calculates the price estimate on a node with newer blocks, causing gas estimation to fail
      // Therefore apply small multiplier for calculating gas estimate
      const priceForEstimation = currentPrice * AUCTION_PRICE_ESTIMATE_MULTIPLIER;
      getEstimatedGasForTransaction(contracts.stickItemTypeAuction.buy, auctionItem.id, {
        from: myWalletAddress,
        value: priceForEstimation
      }).then((estimatedGas) => {
        web3.eth.getBalance(myWalletAddress, (error, balance) => {
          if (error) {
            sendAnalyticsEvent(
              'PurchaseFlow',
              `Auction - ModalDisplayed - Alert (UserTokenBalanceFailed)`,
              `${auctionItem.schema.properties.name.description} - ${eventUserId}`
            );

            reject(`Failed when tried to get user's token balance: ${error}.`);
            return;
          }

          if (balance >= currentPrice) { // Sufficient funds
            Log.info(`Buy auction item: id: ${auctionItem.id}  - address: ${myWalletAddress}.`);

            buyCallback(); // Needed to show confirmation transaction overlay

            contracts.stickItemTypeAuction.buy(auctionItem.id, {
              from: myWalletAddress,
              value: currentPrice,
              gas: estimatedGas
            }, (error, txHash) => {
              if (error) {
                Log.error(`Failed when tried to buy item: ${error}.`);
                reject();
                return;
              }

              resolve(txHash);
            });
          } else {
            sendAnalyticsEvent(
              'PurchaseFlow',
              `Auction - ModalDisplayed - Alert (InsufficientBalance)`,
              `${auctionItem.schema.properties.name.description} - ${eventUserId}`
            );

            reject(INSUFFICIENT_FUNDS);
          }
        });
      }, (error) => {
        reject(error);
      }).catch(error => {
        sendAnalyticsEvent(
          'PurchaseFlow',
          `Auction - ModalDisplayed - Alert (GasEstimateFailed)`,
          `${auctionItem.schema.properties.name.description} - ${eventUserId}`
        );

        Log.error(`Failed when tried to getEstimatedGasForTransaction: ${error}.`);
        reject(error);
      });
    }).catch(error => {
      sendAnalyticsEvent(
        'PurchaseFlow',
        `Auction - ModalDisplayed - Alert (GetAuctionPriceFailed)`,
        `${auctionItem.schema.properties.name.description} - ${eventUserId}`
      );

      Log.error(`Failed when tried to getAuctionPrice: ${error}.`);
      reject(error);
    });
  });

export const buySaleItem = (saleItem, myWalletAddress, web3, eventUserId, buyCallback) =>
  new Promise((resolve, reject) => {
    getEstimatedGasForTransaction(contracts.stickItemTypeSale.buy, saleItem.id, {
      from: myWalletAddress,
      value: saleItem.price
    }).then((estimatedGas) => {
      web3.eth.getBalance(myWalletAddress, (error, result) => {
        const balance = result.toNumber();

        if (error) {
          sendAnalyticsEvent(
            'PurchaseFlow',
            `Sale - ModalDisplayed - Alert (UserTokenBalanceFailed)`,
            `${saleItem.schema.properties.name.description} - ${eventUserId}`
          );

          reject(`Failed when tried to get user's token balance: ${error}.`);
          return;
        }

        if (balance >= saleItem.price) { // Sufficient funds
          Log.info(`Buy sale item: id: ${saleItem.id}  - address: ${myWalletAddress}.`);

          buyCallback(); // Needed to show confirmation transaction overlay

          contracts.stickItemTypeSale.buy(saleItem.id, {
            from: myWalletAddress,
            value: saleItem.price,
            gas: estimatedGas
          }, (error, txHash) => {
            if (error) {
              Log.error(`Failed when tried to buy item: ${error}.`);
              reject();
              return;
            }

            resolve(txHash);
          });
        } else {
          sendAnalyticsEvent(
            'PurchaseFlow',
            `Sale - ModalDisplayed - Alert (InsufficientBalance)`,
            `${saleItem.schema.properties.name.description} - ${eventUserId}`
          );

          reject(INSUFFICIENT_FUNDS);
        }
      });
    }, (error) => {
      sendAnalyticsEvent(
        'PurchaseFlow',
        `Sale - ModalDisplayed - Alert (GasEstimateFailed)`,
        `${saleItem.schema.properties.name.description} - ${eventUserId}`
      );

      reject(error);
    }).catch(error => {
      Log.error(`Failed when tried to getAuctionPrice: ${error}.`);
      reject(error);
    });
  });

export const calculateAuctionPriceAtTime = (startPrice, endPrice, startTime, endTime, time) => {
  // Auction hasn't started yet
  if (startTime > time)
  {
    return startPrice;
  }

  const duration = endTime - startTime;
  const secondsPassed = time - startTime;

  // Auction has finished
  if (secondsPassed >= duration)
  {
    return endPrice;
  }
  else
  {
    const priceDifference = endPrice - startPrice;

    const currentPriceChange = priceDifference * secondsPassed / duration;

    const currentPrice = startPrice + currentPriceChange;

    return currentPrice;
  }
}

export const getCurrentProvider = (web3) => {
  if (web3.currentProvider.isMetaMask)
    return PROVIDERS.METAMASK;

  if (web3.currentProvider.isTrust)
    return PROVIDERS.TRUST;

  if (typeof window.SOFA !== 'undefined')
    return PROVIDERS.TOSHI;

  if (typeof window.__CIPHER__ !== 'undefined')
    return PROVIDERS.CIPHER;

  if (web3.currentProvider.constructor.name === 'EthereumProvider')
    return PROVIDERS.MIST;

  if (web3.currentProvider.constructor.name === 'Web3FrameProvider')
    return PROVIDERS.PARITY;

  if (web3.currentProvider.host && web3.currentProvider.host.indexOf('infura') !== -1)
    return PROVIDERS.INFURA;

  if (web3.currentProvider.host && web3.currentProvider.host.indexOf('localhost') !== -1)
    return PROVIDERS.LOCALHOST;

  return PROVIDERS.UNKNOWN;
}

export const sendAnalyticsEvent = (eventCategory, eventAction, eventLabel, eventValue = 0) => {
  const event = {
    'event': 'GAEvent',
    'eventCategory': eventCategory,
    'eventAction': eventAction,
    'eventLabel': eventLabel,
    'eventValue': eventValue
  };

  Log.info('Sending analytics event:');
  Log.info(event);

  window.dataLayer.push(event);
}


// WEBPACK FOOTER //
// ./src/utils/Utils.js